'use client';

import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useAppStore } from '@/stores/app-store';
import { useAuthStore } from '@/stores/auth-store';
import { offlineDB, type OfflineVoter, type OfflineVerification, type SyncQueueItem } from '@/lib/offline-db';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  CheckCircle2,
  XCircle,
  User,
  Fingerprint,
  Loader2,
  Shield,
  Volume2,
  RotateCcw,
  PenLine,
  Camera,
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

// ==================== HINDI VOICE - PRE-RECORDED MP3 FILES ====================
let thumbBlobUrl: string | null = null;
let signatureBlobUrl: string | null = null;
let audioPreloadPromise: Promise<void> | null = null;

function preloadAudioAsBlobs() {
  if (typeof window === 'undefined') return;
  if (audioPreloadPromise) return audioPreloadPromise;

  audioPreloadPromise = (async () => {
    try {
      const cache = await caches.open('mpsec-voter-register-v8').catch(() => null);

      if (!thumbBlobUrl) {
        try {
          let blob: Blob | null = null;
          if (cache) {
            const cachedRes = await cache.match('/audio/thumb-hi.mp3');
            if (cachedRes && cachedRes.ok) blob = await cachedRes.blob();
          }
          if (!blob) {
            const res = await fetch('/audio/thumb-hi.mp3');
            if (res.ok) blob = await res.blob();
          }
          if (blob) thumbBlobUrl = URL.createObjectURL(blob);
        } catch { /* silent */ }
      }

      if (!signatureBlobUrl) {
        try {
          let blob: Blob | null = null;
          if (cache) {
            const cachedRes = await cache.match('/audio/signature-hi.mp3');
            if (cachedRes && cachedRes.ok) blob = await cachedRes.blob();
          }
          if (!blob) {
            const res = await fetch('/audio/signature-hi.mp3');
            if (res.ok) blob = await res.blob();
          }
          if (blob) signatureBlobUrl = URL.createObjectURL(blob);
        } catch { /* silent */ }
      }
    } catch { /* silent */ }
  })();

  return audioPreloadPromise;
}

if (typeof window !== 'undefined') preloadAudioAsBlobs();

// Global audio reference to prevent overlap
let currentAudio: HTMLAudioElement | null = null;

function stopCurrentAudio() {
  if (currentAudio) {
    try {
      currentAudio.pause();
      currentAudio.currentTime = 0;
      currentAudio = null;
    } catch { /* silent */ }
  }
}

function playConfirmationAudio(type: 'thumb' | 'signature') {
  // Always stop any currently playing audio first to prevent overlap
  stopCurrentAudio();

  try {
    const blobUrl = type === 'thumb' ? thumbBlobUrl : signatureBlobUrl;
    const fallbackPath = type === 'thumb' ? '/audio/thumb-hi.mp3' : '/audio/signature-hi.mp3';
    if (blobUrl) {
      const audio = new Audio(blobUrl);
      currentAudio = audio;
      audio.onended = () => { if (currentAudio === audio) currentAudio = null; };
      audio.play().catch(() => {
        if (currentAudio === audio) currentAudio = null;
        playFromPath(fallbackPath);
      });
    } else {
      playFromPath(fallbackPath);
    }
  } catch {
    currentAudio = null;
    playBeepFallback();
  }
}

function playFromPath(path: string) {
  stopCurrentAudio();
  const audio = new Audio(path);
  currentAudio = audio;
  audio.onended = () => { if (currentAudio === audio) currentAudio = null; };
  audio.play().catch(() => {
    if (currentAudio === audio) currentAudio = null;
    playBeepFallback();
  });
}

function playBeepFallback() {
  try {
    if (typeof window === 'undefined') return;
    const ctx = new (window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext)();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.frequency.value = 800;
    gain.gain.value = 0.3;
    osc.start();
    osc.stop(ctx.currentTime + 0.3);
  } catch { /* silent */ }
}

// ==================== THUMB IMAGE PROCESSING (CAMERA) ====================

interface ThumbBBox {
  x: number;
  y: number;
  width: number;
  height: number;
}

async function detectThumbViaAPI(imageDataUrl: string): Promise<ThumbBBox | null> {
  try {
    // Compress image before sending to VLM to reduce payload size
    let compressedDataUrl = imageDataUrl;
    try {
      const tempImg = new Image();
      await new Promise<void>((resolve, reject) => {
        tempImg.onload = () => resolve();
        tempImg.onerror = () => reject(new Error('Image load failed'));
        tempImg.src = imageDataUrl;
      });
      
      // Resize to max 800px wide for VLM processing
      const maxDim = 800;
      if (tempImg.width > maxDim || tempImg.height > maxDim) {
        const scale = maxDim / Math.max(tempImg.width, tempImg.height);
        const newW = Math.round(tempImg.width * scale);
        const newH = Math.round(tempImg.height * scale);
        const tempCanvas = document.createElement('canvas');
        tempCanvas.width = newW;
        tempCanvas.height = newH;
        const tempCtx = tempCanvas.getContext('2d');
        if (tempCtx) {
          tempCtx.drawImage(tempImg, 0, 0, newW, newH);
          compressedDataUrl = tempCanvas.toDataURL('image/jpeg', 0.7);
        }
      }
    } catch { /* Use original */ }

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 15000); // 15 sec timeout

    const response = await fetch('/api/thumb-detect', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ image: compressedDataUrl }),
      signal: controller.signal,
    });
    clearTimeout(timeoutId);

    if (!response.ok) return null;
    const data = await response.json();
    if (data.success && data.bbox) return data.bbox as ThumbBBox;
    return null;
  } catch {
    return null;
  }
}

/**
 * Crop thumb from camera image, convert to B&W (ink fingerprint style),
 * and compress to ≤30KB.
 */
function cropAndConvertToBW(img: HTMLImageElement, bbox: ThumbBBox | null): string {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  if (!ctx) return '';

  // Step 1: Crop the thumb region
  let cropX: number, cropY: number, cropW: number, cropH: number;

  if (bbox) {
    cropX = Math.round((bbox.x / 100) * img.width);
    cropY = Math.round((bbox.y / 100) * img.height);
    cropW = Math.round((bbox.width / 100) * img.width);
    cropH = Math.round((bbox.height / 100) * img.height);
    // Add padding
    const padX = Math.round(cropW * 0.1);
    const padY = Math.round(cropH * 0.1);
    cropX = Math.max(0, cropX - padX);
    cropY = Math.max(0, cropY - padY);
    cropW = Math.min(img.width - cropX, cropW + padX * 2);
    cropH = Math.min(img.height - cropY, cropH + padY * 2);
  } else {
    // No VLM bbox — use center crop
    const minDim = Math.min(img.width, img.height);
    cropW = Math.round(minDim * 0.4);
    cropH = Math.round(minDim * 0.5);
    cropX = Math.round((img.width - cropW) / 2);
    cropY = Math.round(img.height * 0.25);
  }

  cropW = Math.max(10, Math.min(cropW, img.width - cropX));
  cropH = Math.max(10, Math.min(cropH, img.height - cropY));

  // Step 2: Draw the cropped region to a temp canvas for B&W conversion
  const tempCanvas = document.createElement('canvas');
  const tempCtx = tempCanvas.getContext('2d');
  if (!tempCtx) return '';

  // Output size for B&W processing (larger for detail)
  const processSize = 300;
  tempCanvas.width = processSize;
  tempCanvas.height = processSize;
  tempCtx.fillStyle = '#ffffff';
  tempCtx.fillRect(0, 0, processSize, processSize);

  // Draw the crop centered, fitting inside the square
  const cropAspect = cropW / cropH;
  let drawW: number, drawH: number, drawX: number, drawY: number;
  if (cropAspect > 1) {
    drawW = processSize;
    drawH = Math.round(processSize / cropAspect);
    drawX = 0;
    drawY = Math.round((processSize - drawH) / 2);
  } else {
    drawH = processSize;
    drawW = Math.round(processSize * cropAspect);
    drawX = Math.round((processSize - drawW) / 2);
    drawY = 0;
  }

  tempCtx.drawImage(img, cropX, cropY, cropW, cropH, drawX, drawY, drawW, drawH);

  // Step 3: Convert to grayscale
  const imageData = tempCtx.getImageData(0, 0, processSize, processSize);
  const data = imageData.data;

  for (let i = 0; i < data.length; i += 4) {
    const r = data[i];
    const g = data[i + 1];
    const b = data[i + 2];
    const gray = Math.round(0.299 * r + 0.587 * g + 0.114 * b);
    data[i] = gray;
    data[i + 1] = gray;
    data[i + 2] = gray;
  }

  // Step 4: Apply threshold to make it look like ink fingerprint on white paper
  // Dark pixels become darker, light pixels become white
  // This simulates an inked fingerprint impression
  for (let i = 0; i < data.length; i += 4) {
    const gray = data[i];
    // Threshold: pixels darker than midpoint become very dark, others become white
    // This creates the characteristic B&W fingerprint look
    let newVal: number;
    if (gray < 80) {
      // Very dark areas: ink ridges - make them dark
      newVal = Math.max(0, gray - 30);
    } else if (gray < 140) {
      // Medium: transition zone - increase contrast
      newVal = Math.round(gray * 0.6);
    } else if (gray < 200) {
      // Light: skin with no ink - push toward white
      newVal = Math.min(255, gray + 60);
    } else {
      // Very light: definitely no ink - pure white
      newVal = 255;
    }
    data[i] = newVal;
    data[i + 1] = newVal;
    data[i + 2] = newVal;
  }

  tempCtx.putImageData(imageData, 0, 0);

  // Step 5: Output to final canvas at smaller size for storage
  const outputSize = 200;
  canvas.width = outputSize;
  canvas.height = outputSize;
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, outputSize, outputSize);
  ctx.drawImage(tempCanvas, 0, 0, outputSize, outputSize);

  // Step 6: Compress to JPEG — target ≤30KB
  let quality = 0.7;
  let result = canvas.toDataURL('image/jpeg', quality);
  const maxBase64Len = 30 * 1024 * 1.37;
  while (result.length > maxBase64Len && quality > 0.1) {
    quality -= 0.1;
    result = canvas.toDataURL('image/jpeg', quality);
  }
  console.log(`[Camera] B&W Cropped image: ${Math.round(result.length / 1.37 / 1024)}KB (quality=${quality.toFixed(1)})`);
  return result;
}

// ==================== DYNAMIC COMPOSITING THUMB IMPRESSION ====================
// Architecture: Pre-loaded photorealistic fingerprint PNG assets + real-time
// canvas transformations (rotation, scale, opacity, clipping).
// This guarantees 100% photorealism because the base assets are AI-generated
// from actual fingerprint descriptions, while still providing dynamic randomness
// (rotation, scale, opacity, asset selection) for each voter.

// All fingerprint image paths from both folders
const FINGERPRINT_IMAGES = [
  // thumb-impressions folder (primary — 37 images)
  ...Array.from({ length: 40 }, (_, i) => `/thumb-impressions/thumb_${String(i + 1).padStart(2, '0')}.png`),
  // fingerprints folder (secondary — 21 images)
  '/fingerprints/fp_whorl_01.png',
  '/fingerprints/fp_whorl_02.png',
  '/fingerprints/fp_whorl_03.png',
  '/fingerprints/fp_whorl_04.png',
  '/fingerprints/fp_whorl_05.png',
  '/fingerprints/fp_whorl_06.png',
  '/fingerprints/fp_whorl_07.png',
  '/fingerprints/fp_whorl_08.png',
  '/fingerprints/fp_loop_01.png',
  '/fingerprints/fp_loop_02.png',
  '/fingerprints/fp_loop_03.png',
  '/fingerprints/fp_loop_04.png',
  '/fingerprints/fp_loop_05.png',
  '/fingerprints/fp_loop_06.png',
  '/fingerprints/fp_loop_07.png',
  '/fingerprints/fp_arch_01.png',
  '/fingerprints/fp_arch_03.png',
  '/fingerprints/fp_arch_04.png',
  '/fingerprints/fp_mixed_01.png',
  '/fingerprints/fp_mixed_02.png',
  '/fingerprints/fp_mixed_03.png',
];

// Global image cache — loaded once, reused across renders
const imageCache = new Map<string, HTMLImageElement>();
let imagesLoaded = false;

// Preload all fingerprint images into cache
async function preloadFingerprintImages(): Promise<void> {
  if (imagesLoaded) return;

  const loadPromises = FINGERPRINT_IMAGES.map((src) => {
    return new Promise<void>((resolve) => {
      if (imageCache.has(src)) {
        resolve();
        return;
      }
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        imageCache.set(src, img);
        resolve();
      };
      img.onerror = () => {
        // Image failed to load — skip it silently
        resolve();
      };
      img.src = src;
    });
  });

  await Promise.all(loadPromises);
  imagesLoaded = true;
  console.log(`[Fingerprint] Preloaded ${imageCache.size} fingerprint images`);
}

// Get a random fingerprint image from the cache
function getRandomFingerprintImage(): HTMLImageElement | null {
  const available = Array.from(imageCache.values());
  if (available.length === 0) return null;
  return available[Math.floor(Math.random() * available.length)];
}

// ==================== DYNAMIC COMPOSITING RENDERER ====================
// Draws a photorealistic fingerprint by:
// 1. Randomly selecting a pre-loaded fingerprint image
// 2. Applying rotation based on thumb press direction
// 3. Applying scale variation for natural size difference
// 4. Clipping to the guide ellipse
// 5. Applying radial pressure gradient (darker center, lighter edges)
// 6. Drawing the image onto the canvas with all transformations
function drawDynamicThumbImpression(
  ctx: CanvasRenderingContext2D,
  centerX: number,
  centerY: number,
  clipRx: number,
  clipRy: number,
  pressAngle?: number
): boolean {
  const img = getRandomFingerprintImage();
  if (!img) {
    console.warn('[Fingerprint] No pre-loaded images available');
    return false;
  }

  ctx.save();

  // Clip to the guide ellipse — fingerprint stays inside the circle
  ctx.beginPath();
  ctx.ellipse(centerX, centerY, clipRx, clipRy, 0, 0, Math.PI * 2);
  ctx.clip();

  // White background inside the clip
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(centerX - clipRx - 5, centerY - clipRy - 5, (clipRx + 5) * 2, (clipRy + 5) * 2);

  // === Apply Dynamic Transformations ===

  // 1. Rotation based on press direction
  // The fingerprint pattern rotates to match the thumb's press angle
  const rotation = pressAngle !== undefined
    ? pressAngle * 0.35 + (Math.random() - 0.5) * 8 * (Math.PI / 180)
    : (Math.random() - 0.5) * 12 * (Math.PI / 180); // ±6° random if no angle

  // 2. Scale variation (0.85 - 1.05) — natural size differences between impressions
  const scale = 0.85 + Math.random() * 0.20;

  // 3. Slight position offset for natural placement variation
  const offsetX = (Math.random() - 0.5) * 4;
  const offsetY = (Math.random() - 0.5) * 4;

  // Apply transformations: translate to center, rotate, scale, then draw image centered
  ctx.translate(centerX + offsetX, centerY + offsetY);
  ctx.rotate(rotation);
  ctx.scale(scale, scale);

  // Calculate draw dimensions — fit the image inside the ellipse with some padding
  // The image should fill the ellipse area completely
  const imgAspect = img.naturalWidth / img.naturalHeight;
  const ellipseAspect = clipRx / clipRy;

  let drawW: number;
  let drawH: number;

  // Scale image to cover the ellipse (like CSS object-fit: cover)
  if (imgAspect > ellipseAspect) {
    // Image is wider than ellipse — fit by height
    drawH = clipRy * 2.3; // Slightly larger than ellipse for full coverage
    drawW = drawH * imgAspect;
  } else {
    // Image is taller than ellipse — fit by width
    drawW = clipRx * 2.3;
    drawH = drawW / imgAspect;
  }

  // Draw the fingerprint image centered at origin
  ctx.drawImage(img, -drawW / 2, -drawH / 2, drawW, drawH);

  // === Apply Radial Pressure Gradient ===
  // Real prints are darkest in center, fade naturally at edges
  // This overlay creates the natural ink pressure effect
  ctx.setTransform(1, 0, 0, 1, 0, 0); // Reset transforms for gradient
  ctx.translate(centerX + offsetX, centerY + offsetY);
  ctx.rotate(rotation);

  const pressureGradient = ctx.createRadialGradient(0, 0, Math.min(clipRx, clipRy) * 0.2, 0, 0, Math.max(clipRx, clipRy) * 1.15);
  pressureGradient.addColorStop(0, 'rgba(255, 255, 255, 0)');       // Center: full ink
  pressureGradient.addColorStop(0.45, 'rgba(255, 255, 255, 0)');    // Still full ink
  pressureGradient.addColorStop(0.65, 'rgba(255, 255, 255, 0.04)'); // Barely fading
  pressureGradient.addColorStop(0.75, 'rgba(255, 255, 255, 0.12)'); // Slight fade
  pressureGradient.addColorStop(0.84, 'rgba(255, 255, 255, 0.35)'); // Noticeable fade
  pressureGradient.addColorStop(0.92, 'rgba(255, 255, 255, 0.70)'); // Heavy fade
  pressureGradient.addColorStop(1.0, 'rgba(255, 255, 255, 0.97)');  // Nearly white at edge

  ctx.fillStyle = pressureGradient;
  ctx.fillRect(-drawW / 2, -drawH / 2, drawW, drawH);

  ctx.restore();

  return true;
}

// ==================== FALLBACK: Simple canvas-drawn impression ====================
// Only used if NO fingerprint images are available (should never happen after preload)
function drawSimpleThumbImpression(ctx: CanvasRenderingContext2D, touchX: number, touchY: number) {
  const thumbW = 55 + Math.random() * 12;
  const thumbH = 72 + Math.random() * 14;
  const rotation = (Math.random() - 0.5) * 8 * (Math.PI / 180);

  ctx.save();
  ctx.translate(touchX, touchY);
  ctx.rotate(rotation);

  ctx.save();
  ctx.beginPath();
  ctx.ellipse(0, 0, thumbW + 2, thumbH + 2, 0, 0, Math.PI * 2);
  ctx.clip();

  ctx.fillStyle = '#ffffff';
  ctx.fillRect(-thumbW - 5, -thumbH - 5, (thumbW + 5) * 2, (thumbH + 5) * 2);

  // Simple concentric ellipses as last-resort fallback
  const spacing = 4.5;
  ctx.strokeStyle = '#000000';
  ctx.lineWidth = 2.2;
  ctx.lineCap = 'round';

  const maxR = Math.max(thumbW, thumbH);
  for (let r = spacing; r < maxR; r += spacing) {
    const rx = r * (thumbW / maxR);
    const ry = r * (thumbH / maxR);
    ctx.beginPath();
    ctx.ellipse(0, 0, rx, ry, 0, 0, Math.PI * 2);
    ctx.stroke();
  }

  const gradient = ctx.createRadialGradient(0, 0, Math.min(thumbW, thumbH) * 0.4, 0, 0, Math.max(thumbW, thumbH));
  gradient.addColorStop(0, 'rgba(255, 255, 255, 0)');
  gradient.addColorStop(0.6, 'rgba(255, 255, 255, 0)');
  gradient.addColorStop(0.85, 'rgba(255, 255, 255, 0.5)');
  gradient.addColorStop(1.0, 'rgba(255, 255, 255, 0.97)');
  ctx.fillStyle = gradient;
  ctx.fillRect(-thumbW - 5, -thumbH - 5, (thumbW + 5) * 2, (thumbH + 5) * 2);

  ctx.restore();
  ctx.restore();
}

export function VoterVerifyScreen() {
  const { selectedVoter, setCurrentView, isOnline, setPendingSyncCount } = useAppStore();
  const { token, user } = useAuthStore();
  const { toast } = useToast();

  const voter = selectedVoter as unknown as OfflineVoter | null;
  const boothId = user?.boothId || '';

  const thumbCanvasRef = useRef<HTMLCanvasElement>(null);
  const signatureCanvasRef = useRef<HTMLCanvasElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);

  const [captureMode, setCaptureMode] = useState<'thumb' | 'signature'>('thumb');
  const [thumbMethod, setThumbMethod] = useState<'press' | 'camera'>('press');
  const [hasThumbCapture, setHasThumbCapture] = useState(false);
  const [hasSignatureCapture, setHasSignatureCapture] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [cameraImage, setCameraImage] = useState<string | null>(null);
  const [processedThumbImage, setProcessedThumbImage] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);

  const drawingRef = useRef(false);
  const lastPosRef = useRef<{ x: number; y: number } | null>( null);
  const signatureTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const signatureAudioPlayedRef = useRef(false);

  // Countdown state for thumb press
  const [countdown, setCountdown] = useState<number | null>(null); // null = not counting, 5-0 = counting
  const [countdownProgress, setCountdownProgress] = useState(0); // 0-100 for circular progress
  const countdownRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const countdownStartRef = useRef<number>(0);
  const thumbLockedRef = useRef(false); // Lock after capture to prevent re-press
  const touchActiveRef = useRef(false); // Track if touch is currently active
  const touchPosRef = useRef<{ x: number; y: number } | null>(null);
  const animFrameRef = useRef<number>(0); // for countdown animation

  // Canvas size for thumb capture area
  const CANVAS_SIZE = 360;
  // Guide ellipse — slightly smaller for realistic thumb proportion
  const GUIDE_RX = 72;
  const GUIDE_RY = 94;
  // Countdown duration in milliseconds
  const COUNTDOWN_DURATION = 5000;

  // Initialize canvases and preload fingerprint images
  useEffect(() => {
    initThumbCanvas();
    initSignatureCanvas();
    // Preload fingerprint images for Dynamic Compositing
    preloadFingerprintImages().catch((e) => {
      console.warn('[Fingerprint] Failed to preload images:', e);
    });
  }, []);

  useEffect(() => {
    if (captureMode === 'thumb' && thumbMethod === 'press') {
      initThumbCanvas();
    } else if (captureMode === 'signature') {
      initSignatureCanvas();
    }
  }, [captureMode, thumbMethod]);

  const initThumbCanvas = () => {
    const canvas = thumbCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const dpr = window.devicePixelRatio || 2;
    canvas.width = CANVAS_SIZE * dpr;
    canvas.height = CANVAS_SIZE * dpr;
    canvas.style.width = CANVAS_SIZE + 'px';
    canvas.style.height = CANVAS_SIZE + 'px';
    ctx.scale(dpr, dpr);

    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, CANVAS_SIZE, CANVAS_SIZE);
    drawThumbGuide(ctx, CANVAS_SIZE);
  };

  const drawThumbGuide = useCallback((ctx: CanvasRenderingContext2D, size: number) => {
    const cx = size / 2;
    const cy = size / 2;

    // Outer dashed rectangle
    const padding = 20;
    const boxSize = size - padding * 2;
    ctx.strokeStyle = '#d1d5db';
    ctx.lineWidth = 2;
    ctx.setLineDash([8, 4]);
    ctx.strokeRect(padding, padding, boxSize, boxSize);
    ctx.setLineDash([]);

    // Main thumb outline - LARGER ellipse
    ctx.strokeStyle = '#94a3b8';
    ctx.lineWidth = 2;
    ctx.setLineDash([6, 4]);
    ctx.beginPath();
    ctx.ellipse(cx, cy, GUIDE_RX, GUIDE_RY, 0, 0, Math.PI * 2);
    ctx.stroke();
    ctx.setLineDash([]);

    // Inner ridge suggestion lines
    ctx.strokeStyle = '#cbd5e1';
    ctx.lineWidth = 1;
    for (let i = 0; i < 5; i++) {
      ctx.beginPath();
      ctx.ellipse(cx, cy - 5, 10 + i * 8, 12 + i * 9, 0, Math.PI * 0.12, Math.PI * 0.88);
      ctx.stroke();
    }

    // Fingertip arc at top
    ctx.strokeStyle = '#cbd5e1';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.ellipse(cx, cy - GUIDE_RY + 15, 25, 15, 0, Math.PI * 0.2, Math.PI * 0.8);
    ctx.stroke();

    // Instruction text
    ctx.fillStyle = '#6b7280';
    ctx.font = 'bold 16px sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText('यहाँ अंगूठा दबाएं', cx, size - 14);
  }, [GUIDE_RX, GUIDE_RY]);

  const initSignatureCanvas = () => {
    const canvas = signatureCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const dpr = window.devicePixelRatio || 2;
    const w = Math.min(400, window.innerWidth - 64);
    const h = 180;
    canvas.width = w * dpr;
    canvas.height = h * dpr;
    canvas.style.width = w + 'px';
    canvas.style.height = h + 'px';
    ctx.scale(dpr, dpr);

    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, w, h);

    ctx.strokeStyle = '#e5e7eb';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(20, h - 30);
    ctx.lineTo(w - 20, h - 30);
    ctx.stroke();

    ctx.fillStyle = '#d1d5db';
    ctx.font = '14px sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText('यहाँ हस्ताक्षर करें', w / 2, h / 2);
  };

  // ==================== DRAW COUNTDOWN OVERLAY ON CANVAS ====================
  // Progress ring is on the OUTER SQUARE BOX border (not inside the circle)
  // so the voter can see it even when their thumb covers the center area
  const drawCountdownOverlay = useCallback((secondsLeft: number, progress: number) => {
    const canvas = thumbCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const dpr = window.devicePixelRatio || 2;
    const cx = CANVAS_SIZE / 2;
    const cy = CANVAS_SIZE / 2;

    // Redraw the base (white + guide)
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.scale(dpr, dpr);
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, CANVAS_SIZE, CANVAS_SIZE);
    drawThumbGuide(ctx, CANVAS_SIZE);

    // Semi-transparent overlay inside the guide ellipse to show "pressing"
    ctx.save();
    ctx.beginPath();
    ctx.ellipse(cx, cy, GUIDE_RX, GUIDE_RY, 0, 0, Math.PI * 2);
    ctx.clip();
    ctx.fillStyle = 'rgba(5, 150, 105, 0.08)'; // Very subtle green tint inside
    ctx.fillRect(0, 0, CANVAS_SIZE, CANVAS_SIZE);
    ctx.restore();

    // ===== OUTER SQUARE BOX PROGRESS RING =====
    // This is the main progress indicator - visible even with thumb covering center
    const padding = 20;
    const boxSize = CANVAS_SIZE - padding * 2;
    const boxX = padding;
    const boxY = padding;
    const cornerRadius = 12;

    // Background rounded rectangle (gray track)
    ctx.beginPath();
    ctx.roundRect(boxX, boxY, boxSize, boxSize, cornerRadius);
    ctx.strokeStyle = '#e5e7eb';
    ctx.lineWidth = 10;
    ctx.stroke();

    // Animated progress rounded rectangle (emerald) — draws progressively along the perimeter
    if (progress > 0) {
      const totalPerimeter = 2 * (boxSize + boxSize) - 8 * cornerRadius + 2 * Math.PI * cornerRadius;
      const progressLength = (progress / 100) * totalPerimeter;

      ctx.beginPath();
      ctx.roundRect(boxX, boxY, boxSize, boxSize, cornerRadius);
      ctx.strokeStyle = '#059669';
      ctx.lineWidth = 10;
      ctx.lineCap = 'round';

      // Use dash offset technique for smooth progressive drawing
      ctx.setLineDash([progressLength, totalPerimeter - progressLength]);
      ctx.lineDashOffset = 0;
      ctx.stroke();
      ctx.setLineDash([]);
    }

    // ===== COUNTDOWN NUMBER AT TOP CENTER (above the thumb area) =====
    // Positioned at the top of the square box so it's visible even with thumb covering center
    const numberY = boxY + 30; // Near top of the box

    if (secondsLeft === 0 && progress >= 100) {
      // Show checkmark at completion
      ctx.fillStyle = '#059669';
      ctx.font = 'bold 36px sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText('✓', cx, numberY);

      // "Complete" text
      ctx.fillStyle = '#059669';
      ctx.font = 'bold 14px sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'top';
      ctx.fillText('पूर्ण!', cx, numberY + 22);
    } else {
      // Countdown number
      ctx.fillStyle = '#047857';
      ctx.font = 'bold 40px sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(String(secondsLeft), cx, numberY);

      // "Hold" instruction text
      ctx.fillStyle = '#6b7280';
      ctx.font = 'bold 12px sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'top';
      ctx.fillText('अंगूठा दबाए रखें', cx, numberY + 24);
    }

    // ===== ALSO show seconds remaining at BOTTOM of square box =====
    // This gives another visual cue below the thumb area
    const bottomY = CANVAS_SIZE - padding - 20;

    // Progress percentage text at bottom
    ctx.fillStyle = '#047857';
    ctx.font = 'bold 13px sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(`${Math.round(progress)}% पूर्ण`, cx, bottomY);

    // Pulsing dot indicator at bottom-left corner
    const dotRadius = 5 + Math.sin(Date.now() * 0.005) * 2;
    ctx.beginPath();
    ctx.arc(boxX + 20, bottomY, dotRadius, 0, Math.PI * 2);
    ctx.fillStyle = progress >= 100 ? '#059669' : '#f59e0b';
    ctx.fill();
  }, [CANVAS_SIZE, GUIDE_RX, GUIDE_RY, drawThumbGuide]);

  // ==================== THUMB PRESS CAPTURE WITH COUNTDOWN ====================

  useEffect(() => {
    const canvas = thumbCanvasRef.current;
    if (!canvas || captureMode !== 'thumb' || thumbMethod !== 'press') return;

    const getPos = (e: TouchEvent | MouseEvent) => {
      const rect = canvas.getBoundingClientRect();
      const scaleX = CANVAS_SIZE / rect.width;
      const scaleY = CANVAS_SIZE / rect.height;
      if ('touches' in e && e.touches.length > 0) {
        return {
          x: (e.touches[0].clientX - rect.left) * scaleX,
          y: (e.touches[0].clientY - rect.top) * scaleY,
        };
      }
      if ('clientX' in e) {
        return {
          x: ((e as MouseEvent).clientX - rect.left) * scaleX,
          y: ((e as MouseEvent).clientY - rect.top) * scaleY,
        };
      }
      return null;
    };

    // Check if touch position is inside the guide ellipse
    const isInsideGuideEllipse = (x: number, y: number): boolean => {
      const cx = CANVAS_SIZE / 2;
      const cy = CANVAS_SIZE / 2;
      const nx = (x - cx) / GUIDE_RX;
      const ny = (y - cy) / GUIDE_RY;
      return (nx * nx + ny * ny) <= 1.3; // Slightly larger hit area
    };

    const cancelCountdown = () => {
      if (countdownRef.current) {
        clearInterval(countdownRef.current);
        countdownRef.current = null;
      }
      if (animFrameRef.current) {
        cancelAnimationFrame(animFrameRef.current);
        animFrameRef.current = 0;
      }
      setCountdown(null);
      setCountdownProgress(0);
      touchActiveRef.current = false;
      touchPosRef.current = null;

      // Restore the original guide
      const ctx = canvas.getContext('2d');
      if (ctx) {
        const dpr = window.devicePixelRatio || 2;
        ctx.setTransform(1, 0, 0, 1, 0, 0);
        ctx.scale(dpr, dpr);
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, CANVAS_SIZE, CANVAS_SIZE);
        drawThumbGuide(ctx, CANVAS_SIZE);
      }
    };

    const startCountdown = (touchX: number, touchY: number) => {
      touchActiveRef.current = true;
      touchPosRef.current = { x: touchX, y: touchY };
      countdownStartRef.current = Date.now();

      let lastSecond = 5;
      setCountdown(5);
      setCountdownProgress(0);

      // Draw initial countdown overlay
      drawCountdownOverlay(5, 0);

      // Use requestAnimationFrame for smooth progress animation
      const animateProgress = () => {
        if (!touchActiveRef.current) return; // Touch was lifted

        const elapsed = Date.now() - countdownStartRef.current;
        const progress = Math.min(100, (elapsed / COUNTDOWN_DURATION) * 100);
        const currentSecond = Math.max(0, Math.ceil((COUNTDOWN_DURATION - elapsed) / 1000));

        setCountdownProgress(progress);

        if (currentSecond !== lastSecond && currentSecond >= 0) {
          lastSecond = currentSecond;
          setCountdown(currentSecond);
        }

        drawCountdownOverlay(currentSecond, progress);

        if (elapsed >= COUNTDOWN_DURATION) {
          // Countdown complete! Capture the thumb impression
          completeThumbCapture(touchX, touchY);
          return;
        }

        animFrameRef.current = requestAnimationFrame(animateProgress);
      };

      animFrameRef.current = requestAnimationFrame(animateProgress);
    };

    const completeThumbCapture = (touchX: number, touchY: number) => {
      // Clear countdown state
      if (countdownRef.current) {
        clearInterval(countdownRef.current);
        countdownRef.current = null;
      }
      touchActiveRef.current = false;
      thumbLockedRef.current = true; // LOCK - no more presses allowed

      // Show "0" briefly for visual feedback, then draw impression
      drawCountdownOverlay(0, 100);

      // Calculate the press angle (direction from center to touch point)
      // This determines the orientation of the fingerprint ridges
      const cx = CANVAS_SIZE / 2;
      const cy = CANVAS_SIZE / 2;
      const pressAngle = Math.atan2(touchY - cy, touchX - cx);

      setTimeout(() => {
        setCountdown(null);
        setCountdownProgress(0);

        // Draw the thumb impression ALWAYS at center of guide circle
        // Pass the press angle so ridges orient based on thumb press direction
        clearThumbGuide();
        const centerX = CANVAS_SIZE / 2;
        const centerY = CANVAS_SIZE / 2;
        drawThumbImpression(centerX, centerY, pressAngle);
        setHasThumbCapture(true);

        // Play confirmation audio
        playConfirmationAudio('thumb');

        toast({
          title: 'सफल',
          description: 'अंगूठे का निशान कैप्चर हो गया है',
        });
      }, 300); // Brief 300ms pause at "0" before showing the impression
    };

    const handleStart = (e: TouchEvent | MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();

      // BLOCK: If already captured, ignore
      if (thumbLockedRef.current || hasThumbCapture) return;
      // BLOCK: If countdown already running, ignore
      if (touchActiveRef.current) return;

      const pos = getPos(e);
      if (!pos) return;

      // Only start countdown if touch is inside the guide ellipse
      if (!isInsideGuideEllipse(pos.x, pos.y)) return;

      startCountdown(pos.x, pos.y);
    };

    const handleMove = (e: TouchEvent | MouseEvent) => {
      if (!touchActiveRef.current) return;
      e.preventDefault();
      e.stopPropagation();
    };

    const handleEnd = (e: TouchEvent | MouseEvent) => {
      // If thumb was already captured, ignore
      if (thumbLockedRef.current || hasThumbCapture) {
        e.preventDefault();
        return;
      }

      e.preventDefault();

      // If countdown was running and user lifted too early → CANCEL, do NOT capture
      if (touchActiveRef.current) {
        cancelCountdown();

        toast({
          title: 'अंगूठा हटा लिया',
          description: 'कृपया 5 सेकंड तक अंगूठा दबाए रखें',
          variant: 'destructive',
        });
      }
    };

    canvas.addEventListener('touchstart', handleStart, { passive: false });
    canvas.addEventListener('touchmove', handleMove, { passive: false });
    canvas.addEventListener('touchend', handleEnd, { passive: false });
    canvas.addEventListener('mousedown', handleStart);
    canvas.addEventListener('mousemove', handleMove);
    canvas.addEventListener('mouseup', handleEnd);

    return () => {
      canvas.removeEventListener('touchstart', handleStart);
      canvas.removeEventListener('touchmove', handleMove);
      canvas.removeEventListener('touchend', handleEnd);
      canvas.removeEventListener('mousedown', handleStart);
      canvas.removeEventListener('mousemove', handleMove);
      canvas.removeEventListener('mouseup', handleEnd);
      // Clean up countdown on unmount
      if (countdownRef.current) {
        clearInterval(countdownRef.current);
      }
      if (animFrameRef.current) {
        cancelAnimationFrame(animFrameRef.current);
      }
    };
  }, [captureMode, thumbMethod, hasThumbCapture, drawCountdownOverlay]);

  const clearThumbGuide = () => {
    const canvas = thumbCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const dpr = window.devicePixelRatio || 2;
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.scale(dpr, dpr);
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, CANVAS_SIZE, CANVAS_SIZE);
  };

  // ==================== DRAW THUMB IMPRESSION ====================
  // PRIMARY: Dynamic Compositing — photorealistic fingerprint PNG asset with transformations
  // FALLBACK: Simple canvas-drawn impression (only if no images loaded)
  // Both: clip the impression to the thumb guide circle so nothing shows outside
  const drawThumbImpression = (touchX: number, touchY: number, pressAngle?: number) => {
    const canvas = thumbCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const dpr = window.devicePixelRatio || 2;

    // Prepare canvas context
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.scale(dpr, dpr);

    // Clear canvas to white
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, CANVAS_SIZE, CANVAS_SIZE);

    // Clip dimensions — slightly inside the guide circle
    const cx = CANVAS_SIZE / 2;
    const cy = CANVAS_SIZE / 2;
    const clipRx = GUIDE_RX - 3;
    const clipRy = GUIDE_RY - 3;

    let drawn = false;

    // Strategy 1: Dynamic Compositing with pre-loaded fingerprint images (PRIMARY)
    try {
      drawn = drawDynamicThumbImpression(ctx, cx, cy, clipRx, clipRy, pressAngle);
      if (drawn) {
        console.log('[Thumb] Drew from photorealistic fingerprint asset');
      }
    } catch (e) {
      console.warn('[Thumb] Dynamic compositing failed:', e);
    }

    // Strategy 2: Simple canvas-drawn fallback (only if no images loaded)
    if (!drawn) {
      try {
        drawSimpleThumbImpression(ctx, touchX, touchY);
        drawn = true;
        console.log('[Thumb] Drew from simple canvas fallback');
      } catch (e) {
        console.error('[Thumb] Even fallback failed:', e);
      }
    }

    // Redraw the guide circle outline on top so the circle boundary is visible
    ctx.strokeStyle = '#94a3b8';
    ctx.lineWidth = 2;
    ctx.setLineDash([6, 4]);
    ctx.beginPath();
    ctx.ellipse(cx, cy, GUIDE_RX, GUIDE_RY, 0, 0, Math.PI * 2);
    ctx.stroke();
    ctx.setLineDash([]);
  };

  // ==================== CAMERA CAPTURE (REAR CAMERA ONLY) ====================

  const handleCameraCapture = () => {
    setCameraImage(null);
    setProcessedThumbImage(null);
    cameraInputRef.current?.click();
  };

  const handleCameraImageSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsProcessing(true);

    const reader = new FileReader();
    reader.onload = async (event) => {
      const dataUrl = event.target?.result as string;
      if (!dataUrl) return;

      setCameraImage(dataUrl);

      const img = new Image();
      img.onload = async () => {
        try {
          // ALWAYS try VLM API for thumb detection
          let bbox: ThumbBBox | null = null;

          try {
            bbox = await detectThumbViaAPI(dataUrl);
            if (bbox) {
              console.log('[Camera] VLM detected thumb at:', JSON.stringify(bbox));
            } else {
              console.log('[Camera] VLM did not find thumb, using fallback crop');
            }
          } catch (e) {
            console.warn('[Camera] VLM detection failed:', e);
          }

          // Crop + convert to B&W fingerprint style
          const processedDataUrl = cropAndConvertToBW(img, bbox);
          setProcessedThumbImage(processedDataUrl);

          if (processedDataUrl) {
            const processedImg = new Image();
            processedImg.onload = () => {
              const canvas = thumbCanvasRef.current;
              if (!canvas) return;
              const ctx = canvas.getContext('2d');
              if (!ctx) return;

              const dpr = window.devicePixelRatio || 2;

              ctx.setTransform(1, 0, 0, 1, 0, 0);
              canvas.width = CANVAS_SIZE * dpr;
              canvas.height = CANVAS_SIZE * dpr;
              ctx.scale(dpr, dpr);

              ctx.fillStyle = '#ffffff';
              ctx.fillRect(0, 0, CANVAS_SIZE, CANVAS_SIZE);

              // Clip to the thumb guide circle — camera image must stay inside the circle
              const cx = CANVAS_SIZE / 2;
              const cy = CANVAS_SIZE / 2;
              ctx.save();
              ctx.beginPath();
              ctx.ellipse(cx, cy, GUIDE_RX - 3, GUIDE_RY - 3, 0, 0, Math.PI * 2);
              ctx.clip();

              // Draw the B&W thumb image centered, filling the circle area
              const maxW = (GUIDE_RX - 3) * 2;
              const maxH = (GUIDE_RY - 3) * 2;

              // Fit the processed image within the circle while maintaining aspect ratio
              const imgAspect = processedImg.width / processedImg.height;
              let drawW: number, drawH: number;
              if (imgAspect > maxW / maxH) {
                drawW = maxW;
                drawH = maxW / imgAspect;
              } else {
                drawH = maxH;
                drawW = maxH * imgAspect;
              }
              const drawX = cx - drawW / 2;
              const drawY = cy - drawH / 2;
              ctx.drawImage(processedImg, drawX, drawY, drawW, drawH);

              ctx.restore(); // Remove circle clip

              // Redraw the guide circle outline on top
              ctx.strokeStyle = '#94a3b8';
              ctx.lineWidth = 2;
              ctx.setLineDash([6, 4]);
              ctx.beginPath();
              ctx.ellipse(cx, cy, GUIDE_RX, GUIDE_RY, 0, 0, Math.PI * 2);
              ctx.stroke();
              ctx.setLineDash([]);

              setHasThumbCapture(true);
              setIsProcessing(false);

              playConfirmationAudio('thumb');

              toast({
                title: 'सफल',
                description: bbox
                  ? 'अंगूठे की पहचान हो गई — केवल अंगूठे का चित्र सहेजा गया'
                  : 'अंगूठे का निशान कैप्चर हो गया है',
              });
            };
            processedImg.src = processedDataUrl;
          } else {
            setIsProcessing(false);
            toast({
              title: 'त्रुटि',
              description: 'छवि प्रसंस्करण विफल। पुनः प्रयास करें।',
              variant: 'destructive',
            });
          }
        } catch {
          setIsProcessing(false);
          toast({
            title: 'त्रुटि',
            description: 'छवि प्रसंस्करण विफल। पुनः प्रयास करें।',
            variant: 'destructive',
          });
        }
      };
      img.src = dataUrl;
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  // ==================== SIGNATURE PAD ====================

  useEffect(() => {
    const canvas = signatureCanvasRef.current;
    if (!canvas || captureMode !== 'signature') return;

    const getPos = (e: TouchEvent | MouseEvent): { x: number; y: number } | null => {
      const rect = canvas.getBoundingClientRect();
      const w = Math.min(400, window.innerWidth - 64);
      const h = 180;
      const scaleX = w / rect.width;
      const scaleY = h / rect.height;
      if ('touches' in e && e.touches.length > 0) {
        return {
          x: (e.touches[0].clientX - rect.left) * scaleX,
          y: (e.touches[0].clientY - rect.top) * scaleY,
        };
      }
      if ('clientX' in e) {
        return {
          x: ((e as MouseEvent).clientX - rect.left) * scaleX,
          y: ((e as MouseEvent).clientY - rect.top) * scaleY,
        };
      }
      return null;
    };

    const clearGuide = () => {
      if (!hasSignatureCapture) {
        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        const dpr = window.devicePixelRatio || 2;
        const w = Math.min(400, window.innerWidth - 64);
        const h = 180;
        ctx.setTransform(1, 0, 0, 1, 0, 0);
        ctx.scale(dpr, dpr);
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, w, h);
        ctx.strokeStyle = '#e5e7eb';
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(20, h - 30);
        ctx.lineTo(w - 20, h - 30);
        ctx.stroke();
        setHasSignatureCapture(true);
      }
    };

    const handleStart = (e: TouchEvent | MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      drawingRef.current = true;
      clearGuide();
      if (signatureTimerRef.current) {
        clearTimeout(signatureTimerRef.current);
        signatureTimerRef.current = null;
      }
      const pos = getPos(e);
      if (pos) {
        lastPosRef.current = pos;
        const ctx = canvas.getContext('2d');
        if (ctx) {
          ctx.beginPath();
          ctx.moveTo(pos.x, pos.y);
          ctx.strokeStyle = '#000000';
          ctx.lineWidth = 2.5;
          ctx.lineCap = 'round';
          ctx.lineJoin = 'round';
        }
      }
    };

    const handleMove = (e: TouchEvent | MouseEvent) => {
      if (!drawingRef.current) return;
      e.preventDefault();
      e.stopPropagation();
      if (signatureTimerRef.current) {
        clearTimeout(signatureTimerRef.current);
        signatureTimerRef.current = null;
      }
      const pos = getPos(e);
      if (pos && lastPosRef.current) {
        const ctx = canvas.getContext('2d');
        if (ctx) {
          ctx.beginPath();
          ctx.moveTo(lastPosRef.current.x, lastPosRef.current.y);
          ctx.lineTo(pos.x, pos.y);
          ctx.strokeStyle = '#000000';
          ctx.lineWidth = 2.5;
          ctx.lineCap = 'round';
          ctx.lineJoin = 'round';
          ctx.stroke();
        }
        lastPosRef.current = pos;
      }
    };

    const handleEnd = (e: TouchEvent | MouseEvent) => {
      e.preventDefault();
      drawingRef.current = false;
      lastPosRef.current = null;

      if (hasSignatureCapture) {
        if (signatureTimerRef.current) {
          clearTimeout(signatureTimerRef.current);
        }
        signatureTimerRef.current = setTimeout(() => {
          if (!signatureAudioPlayedRef.current) {
            signatureAudioPlayedRef.current = true;
            playConfirmationAudio('signature');
            toast({
              title: 'सफल',
              description: 'हस्ताक्षर कैप्चर हो गया है',
            });
          }
        }, 5000);
      }
    };

    canvas.addEventListener('touchstart', handleStart, { passive: false });
    canvas.addEventListener('touchmove', handleMove, { passive: false });
    canvas.addEventListener('touchend', handleEnd, { passive: false });
    canvas.addEventListener('mousedown', handleStart);
    canvas.addEventListener('mousemove', handleMove);
    canvas.addEventListener('mouseup', handleEnd);
    canvas.addEventListener('mouseleave', handleEnd);

    return () => {
      canvas.removeEventListener('touchstart', handleStart);
      canvas.removeEventListener('touchmove', handleMove);
      canvas.removeEventListener('touchend', handleEnd);
      canvas.removeEventListener('mousedown', handleStart);
      canvas.removeEventListener('mousemove', handleMove);
      canvas.removeEventListener('mouseup', handleEnd);
      canvas.removeEventListener('mouseleave', handleEnd);
    };
  }, [captureMode, hasSignatureCapture]);

  // ==================== CANVAS OPERATIONS ====================

  const clearCurrentCanvas = useCallback(() => {
    if (captureMode === 'thumb') {
      setHasThumbCapture(false);
      setCameraImage(null);
      setProcessedThumbImage(null);
      setIsProcessing(false);
      thumbLockedRef.current = false; // Unlock so user can try again
      touchActiveRef.current = false;
      setCountdown(null);
      setCountdownProgress(0);
      if (countdownRef.current) {
        clearInterval(countdownRef.current);
        countdownRef.current = null;
      }
      if (animFrameRef.current) {
        cancelAnimationFrame(animFrameRef.current);
        animFrameRef.current = 0;
      }
      initThumbCanvas();
    } else {
      setHasSignatureCapture(false);
      signatureAudioPlayedRef.current = false;
      if (signatureTimerRef.current) {
        clearTimeout(signatureTimerRef.current);
        signatureTimerRef.current = null;
      }
      initSignatureCanvas();
    }
  }, [captureMode, thumbMethod]);

  const captureCanvasImage = useCallback((): { thumbImage: string; signatureImage: string; verificationType: string } => {
    let thumbImage = '';
    let signatureImage = '';
    const verificationType = captureMode;

    if (captureMode === 'thumb') {
      if (thumbMethod === 'camera' && processedThumbImage) {
        thumbImage = processedThumbImage;
      } else {
        const canvas = thumbCanvasRef.current;
        if (canvas && hasThumbCapture) {
          // Compress to ensure ≤30KB
          let quality = 0.6;
          let result = canvas.toDataURL('image/jpeg', quality);
          const maxBase64Len = 30 * 1024 * 1.37;
          while (result.length > maxBase64Len && quality > 0.1) {
            quality -= 0.1;
            result = canvas.toDataURL('image/jpeg', quality);
          }
          thumbImage = result;
        }
      }
    } else {
      const canvas = signatureCanvasRef.current;
      if (canvas && hasSignatureCapture) {
        let quality = 0.6;
        let result = canvas.toDataURL('image/jpeg', quality);
        const maxBase64Len = 30 * 1024 * 1.37;
        while (result.length > maxBase64Len && quality > 0.1) {
          quality -= 0.1;
          result = canvas.toDataURL('image/jpeg', quality);
        }
        signatureImage = result;
      }
    }

    return { thumbImage, signatureImage, verificationType };
  }, [captureMode, thumbMethod, hasThumbCapture, hasSignatureCapture, processedThumbImage]);

  // ==================== GENDER DISPLAY ====================

  const getGenderDisplay = (gender: string) => {
    if (!gender) return '-';
    const g = gender.toLowerCase();
    if (g === 'male' || g === 'पुरुष') return 'पुरुष';
    if (g === 'female' || g === 'महिला') return 'महिला';
    if (g === 'other' || g === 'अन्य') return 'अन्य';
    return gender;
  };

  // ==================== VERIFY ====================

  const handleVerify = useCallback(async () => {
    if (!voter) {
      toast({ title: 'त्रुटि', description: 'मतदाता की जानकारी नहीं मिली', variant: 'destructive' });
      return;
    }

    const hasCaptured = captureMode === 'thumb' ? hasThumbCapture : hasSignatureCapture;
    if (!hasCaptured) {
      toast({
        title: captureMode === 'thumb' ? 'अंगूठे का निशान आवश्यक' : 'हस्ताक्षर आवश्यक',
        description: captureMode === 'thumb' ? 'कृपया अंगूठे का निशान लें' : 'कृपया हस्ताक्षर करें',
        variant: 'destructive',
      });
      return;
    }

    setIsVerifying(true);

    try {
      const { thumbImage, signatureImage, verificationType } = captureCanvasImage();
      const verificationId = `vrf_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;

      const verification: OfflineVerification = {
        id: verificationId,
        epicNo: voter.epicNo,
        serialNo: voter.serialNo,
        voterName: voter.nameHindi || voter.name,
        fatherHusbandName: voter.fatherHusbandName,
        relationType: voter.relationType || 'पिता',
        gender: voter.gender,
        age: voter.age,
        signatureImage,
        thumbImage,
        verificationType,
        pollingBoothId: boothId,
        officerId: user?.id || '',
        officerName: user?.name || '',
        verifiedAt: new Date().toISOString(),
        synced: 0,
        syncedAt: null,
      };

      await offlineDB.saveVerification(verification);

      const syncItem: SyncQueueItem = {
        id: `sq_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`,
        type: 'verification',
        data: verification,
        createdAt: new Date().toISOString(),
        retryCount: 0,
      };
      await offlineDB.addToSyncQueue(syncItem);

      if (isOnline && token) {
        try {
          const response = await fetch('/api/verifications', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
              epicNo: verification.epicNo,
              serialNo: verification.serialNo,
              voterName: verification.voterName,
              fatherHusbandName: verification.fatherHusbandName,
              relationType: verification.relationType,
              gender: verification.gender,
              age: verification.age,
              signatureImage: verification.signatureImage,
              thumbImage: verification.thumbImage,
              verificationType: verification.verificationType,
              pollingBoothId: verification.pollingBoothId,
              officerId: verification.officerId,
              officerName: verification.officerName,
            }),
          });

          if (response.ok) {
            await offlineDB.markVerificationsSynced([verificationId]);
            await offlineDB.removeFromSyncQueue([syncItem.id]);
          }
        } catch (apiError) {
          console.error('API verification save failed (saved offline):', apiError);
        }
      }

      const pendingCount = await offlineDB.getUnsyncedCount(boothId);
      setPendingSyncCount(pendingCount);

      toast({ title: 'सत्यापन सफल', description: `${voter.nameHindi || voter.name} का सत्यापन हो गया है` });
      setCurrentView('voter-search');
    } catch (error) {
      console.error('Verification error:', error);
      toast({ title: 'सत्यापन विफल', description: 'कृपया पुनः प्रयास करें', variant: 'destructive' });
    } finally {
      setIsVerifying(false);
    }
  }, [voter, captureMode, thumbMethod, hasThumbCapture, hasSignatureCapture, captureCanvasImage, boothId, user, isOnline, token, setCurrentView, setPendingSyncCount, toast]);

  const handleCancel = useCallback(() => {
    setCurrentView('voter-search');
  }, [setCurrentView]);

  const getInitials = (name: string) => {
    if (!name) return '?';
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    return name.substring(0, 2).toUpperCase();
  };

  const canVerify = captureMode === 'thumb' ? hasThumbCapture : hasSignatureCapture;

  if (!voter) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-4">
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="pt-6 pb-6 text-center">
            <XCircle className="w-10 h-10 text-amber-500 mx-auto mb-2" />
            <p className="font-semibold text-amber-800">मतदाता की जानकारी नहीं मिली</p>
            <Button onClick={handleCancel} className="mt-4 bg-emerald-600 hover:bg-emerald-700 text-white">
              वापस जाएं
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-3 sm:px-4 py-4 space-y-4">
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={handleCameraImageSelected}
        aria-label="Rear camera capture"
      />

      {/* Header */}
      <Card className="border-emerald-200 shadow-sm bg-gradient-to-r from-emerald-700 to-emerald-600">
        <CardContent className="pt-4 pb-4">
          <div className="flex items-center gap-3 text-white">
            <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
              <Shield className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-lg font-bold">मतदाता सत्यापन</h2>
              <p className="text-xs text-emerald-200">Voter Verification</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Voter Information Summary */}
      <Card className="border-emerald-200 shadow-sm">
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <User className="w-5 h-5 text-emerald-600" />
            <CardTitle className="text-base text-emerald-800">मतदाता की जानकारी</CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex gap-3 sm:gap-4">
            <div className="flex-shrink-0">
              {voter.photoUrl ? (
                <img src={voter.photoUrl} alt={voter.name} className="w-16 h-20 sm:w-20 sm:h-24 object-cover rounded border border-gray-200" />
              ) : (
                <div className="w-16 h-20 sm:w-20 sm:h-24 bg-emerald-50 rounded border-2 border-dashed border-emerald-200 flex items-center justify-center">
                  <span className="text-xl sm:text-2xl font-bold text-emerald-300">{getInitials(voter.nameHindi || voter.name)}</span>
                </div>
              )}
            </div>
            <div className="flex-1 space-y-1 sm:space-y-1.5 min-w-0">
              <div className="flex gap-2">
                <span className="text-xs sm:text-sm text-gray-500 min-w-[80px] sm:min-w-[100px]">नाम:</span>
                <span className="text-xs sm:text-sm font-bold text-emerald-800 truncate">{voter.nameHindi || voter.name}</span>
              </div>
              <div className="flex gap-2">
                <span className="text-xs sm:text-sm text-gray-500 min-w-[80px] sm:min-w-[100px]">EPiC नंबर:</span>
                <span className="text-xs sm:text-sm font-medium text-gray-800">{voter.epicNo}</span>
              </div>
              <div className="flex gap-2">
                <span className="text-xs sm:text-sm text-gray-500 min-w-[80px] sm:min-w-[100px]">अनुक्रमांक:</span>
                <span className="text-xs sm:text-sm font-medium text-gray-800">{voter.serialNo}</span>
              </div>
              <div className="flex gap-2">
                <span className="text-xs sm:text-sm text-gray-500 min-w-[80px] sm:min-w-[100px]">पिता/पति:</span>
                <span className="text-xs sm:text-sm font-medium text-gray-800 truncate">{voter.fatherHusbandName}</span>
              </div>
              <div className="flex gap-3 sm:gap-4">
                <div className="flex gap-2">
                  <span className="text-xs sm:text-sm text-gray-500">लिंग:</span>
                  <span className="text-xs sm:text-sm font-medium text-gray-800">{getGenderDisplay(voter.gender)}</span>
                </div>
                <div className="flex gap-2">
                  <span className="text-xs sm:text-sm text-gray-500">आयु:</span>
                  <span className="text-xs sm:text-sm font-medium text-gray-800">{voter.age} वर्ष</span>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Capture Mode Toggle */}
      <Card className="border-emerald-200 shadow-sm">
        <CardContent className="pt-4 pb-3">
          <div className="flex items-center gap-2 mb-3">
            <Fingerprint className="w-5 h-5 text-emerald-600" />
            <span className="text-sm font-semibold text-emerald-800">कैप्चर मोड चुनें</span>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <button
              onClick={() => { setCaptureMode('thumb'); setHasThumbCapture(false); setHasSignatureCapture(false); setCameraImage(null); setProcessedThumbImage(null); signatureAudioPlayedRef.current = false; thumbLockedRef.current = false; touchActiveRef.current = false; setCountdown(null); setCountdownProgress(0); if (signatureTimerRef.current) { clearTimeout(signatureTimerRef.current); signatureTimerRef.current = null; } }}
              className={`flex items-center justify-center gap-2 p-3 rounded-xl border-2 transition-all min-h-[52px] ${captureMode === 'thumb' ? 'border-emerald-500 bg-emerald-50 text-emerald-800 shadow-sm' : 'border-gray-200 text-gray-600 hover:border-emerald-300'}`}
            >
              <Fingerprint className="w-5 h-5" />
              <div className="text-left">
                <p className="text-sm font-bold">अंगूठे का निशान लें</p>
                <p className="text-[10px] opacity-70">Thumb Impression</p>
              </div>
            </button>
            <button
              onClick={() => { setCaptureMode('signature'); setHasSignatureCapture(false); setHasThumbCapture(false); setCameraImage(null); setProcessedThumbImage(null); signatureAudioPlayedRef.current = false; thumbLockedRef.current = false; touchActiveRef.current = false; setCountdown(null); setCountdownProgress(0); if (signatureTimerRef.current) { clearTimeout(signatureTimerRef.current); signatureTimerRef.current = null; } }}
              className={`flex items-center justify-center gap-2 p-3 rounded-xl border-2 transition-all min-h-[52px] ${captureMode === 'signature' ? 'border-emerald-500 bg-emerald-50 text-emerald-800 shadow-sm' : 'border-gray-200 text-gray-600 hover:border-emerald-300'}`}
            >
              <PenLine className="w-5 h-5" />
              <div className="text-left">
                <p className="text-sm font-bold">हस्ताक्षर लें</p>
                <p className="text-[10px] opacity-70">Signature</p>
              </div>
            </button>
          </div>
        </CardContent>
      </Card>

      {/* Capture Area */}
      <Card className="border-emerald-200 shadow-sm">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {captureMode === 'thumb' ? <Fingerprint className="w-5 h-5 text-emerald-600" /> : <PenLine className="w-5 h-5 text-emerald-600" />}
              <CardTitle className="text-base text-emerald-800">
                {captureMode === 'thumb' ? 'अंगूठे का निशान' : 'हस्ताक्षर करें'}
              </CardTitle>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={clearCurrentCanvas}
              className="text-xs h-8"
            >
              <RotateCcw className="w-3 h-3 mr-1" />
              रीसेट
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Thumb Method Selection */}
          {captureMode === 'thumb' && (
            <div className="space-y-3">
              <div className="flex gap-2">
                <button
                  onClick={() => { setThumbMethod('press'); setHasThumbCapture(false); setCameraImage(null); setProcessedThumbImage(null); thumbLockedRef.current = false; touchActiveRef.current = false; setCountdown(null); setCountdownProgress(0); }}
                  className={`flex-1 py-2.5 px-3 rounded-lg border-2 text-sm font-medium transition-all ${
                    thumbMethod === 'press'
                      ? 'border-emerald-500 bg-emerald-50 text-emerald-800'
                      : 'border-gray-200 text-gray-500 hover:border-emerald-300'
                  }`}
                >
                  <div className="flex items-center justify-center gap-2">
                    <Fingerprint className="w-4 h-4" />
                    <span>स्क्रीन पर दबाएं</span>
                  </div>
                </button>
                <button
                  onClick={() => { setThumbMethod('camera'); setHasThumbCapture(false); setCameraImage(null); setProcessedThumbImage(null); thumbLockedRef.current = false; touchActiveRef.current = false; setCountdown(null); setCountdownProgress(0); }}
                  className={`flex-1 py-2.5 px-3 rounded-lg border-2 text-sm font-medium transition-all ${
                    thumbMethod === 'camera'
                      ? 'border-emerald-500 bg-emerald-50 text-emerald-800'
                      : 'border-gray-200 text-gray-500 hover:border-emerald-300'
                  }`}
                >
                  <div className="flex items-center justify-center gap-2">
                    <Camera className="w-4 h-4" />
                    <span>कैमरा</span>
                  </div>
                </button>
              </div>

              {/* Camera capture button */}
              {thumbMethod === 'camera' && (
                <Button
                  onClick={handleCameraCapture}
                  disabled={isProcessing}
                  className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
                >
                  {isProcessing ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      प्रसंस्करण हो रहा है...
                    </>
                  ) : (
                    <>
                      <Camera className="w-4 h-4 mr-2" />
                      कैमरे से अंगूठा कैप्चर करें
                    </>
                  )}
                </Button>
              )}
            </div>
          )}

          {/* Canvas area */}
          <div className="flex flex-col items-center gap-3">
            {captureMode === 'thumb' ? (
              <div className="relative border-2 border-gray-200 rounded-xl overflow-hidden bg-white" style={{ maxWidth: '100%' }}>
                <canvas
                  ref={thumbCanvasRef}
                  className={`touch-none block mx-auto ${hasThumbCapture ? 'cursor-default' : countdown !== null ? 'cursor-wait' : 'cursor-pointer'}`}
                  style={{ maxWidth: '100%', height: 'auto' }}
                />
                {isProcessing && (
                  <div className="absolute inset-0 bg-white/80 flex items-center justify-center">
                    <div className="text-center">
                      <Loader2 className="w-8 h-8 animate-spin text-emerald-600 mx-auto mb-2" />
                      <p className="text-sm text-emerald-700 font-medium">अंगूठा पहचान रहा है...</p>
                      <p className="text-xs text-gray-500">कृपया प्रतीक्षा करें</p>
                    </div>
                  </div>
                )}
                {/* Captured overlay - blocks interaction */}
                {hasThumbCapture && thumbMethod === 'press' && (
                  <div className="absolute inset-0 bg-transparent" style={{ pointerEvents: 'all', cursor: 'default' }} />
                )}
              </div>
            ) : (
              <div className="relative border-2 border-gray-200 rounded-xl overflow-hidden bg-white">
                <canvas
                  ref={signatureCanvasRef}
                  className="touch-none cursor-crosshair block"
                  style={{ maxWidth: '100%', height: 'auto' }}
                />
              </div>
            )}

            {/* Status indicator */}
            {captureMode === 'thumb' && hasThumbCapture && (
              <div className="flex items-center gap-2 text-emerald-600">
                <CheckCircle2 className="w-5 h-5" />
                <span className="text-sm font-medium">अंगूठे का निशान कैप्चर हो गया</span>
              </div>
            )}
            {captureMode === 'signature' && hasSignatureCapture && (
              <div className="flex items-center gap-2 text-emerald-600">
                <CheckCircle2 className="w-5 h-5" />
                <span className="text-sm font-medium">हस्ताक्षर कैप्चर हो गया</span>
              </div>
            )}
          </div>

          {/* Instructions */}
          <div className="bg-gray-50 rounded-lg p-3">
            {captureMode === 'thumb' ? (
              <div className="text-xs text-gray-600 space-y-1">
                {thumbMethod === 'press' ? (
                  <>
                    <p className="font-medium text-gray-700">📱 निर्देश:</p>
                    <p>• गोल घेरे के अंदर अंगूठा दबाएं</p>
                    <p>• <strong>5 सेकंड</strong> तक अंगूठा दबाए रखें</p>
                    <p>• काउंटडाउन पूरा होने पर निशान बनेगा</p>
                    <p>• जल्दी हटाने पर निशान नहीं बनेगा</p>
                  </>
                ) : (
                  <>
                    <p className="font-medium text-gray-700">📸 निर्देश:</p>
                    <p>• कैमरे से अंगूठे की तस्वीर लें</p>
                    <p>• AI स्वचालित रूप से केवल अंगूठा पहचानेगा</p>
                    <p>• छवि काले-सफेद में बदल जाएगी</p>
                  </>
                )}
              </div>
            ) : (
              <div className="text-xs text-gray-600 space-y-1">
                <p className="font-medium text-gray-700">✍️ निर्देश:</p>
                <p>• सफेद क्षेत्र में हस्ताक्षर करें</p>
                <p>• 5 सेकंड के बाद स्वचालित रूप से सहेजा जाएगा</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Action Buttons */}
      <Card className="border-emerald-200 shadow-sm">
        <CardContent className="pt-4 pb-4">
          <div className="flex gap-3">
            <Button
              onClick={handleCancel}
              variant="outline"
              className="flex-1 border-gray-300"
            >
              <XCircle className="w-4 h-4 mr-2" />
              रद्द करें
            </Button>
            <Button
              onClick={handleVerify}
              disabled={!canVerify || isVerifying}
              className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white"
            >
              {isVerifying ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  सत्यापन हो रहा है...
                </>
              ) : (
                <>
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                  सत्यापन करें
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
